<script lang="ts">
  /**
   * Une jauge en cases, à cliquer.
   *
   * Le livre ne parle pas d'un nombre mais d'**emplacements** : une Jauge de
   * Stress « constituée d'emplacements de Points de Stress ». La fiche papier
   * les imprime en petites cases qu'on noircit au crayon ; les redessiner ici
   * dit la même chose qu'un « 3 sur 8 », en plus rapide à lire et à remplir —
   * un clic sur la cinquième case pose la valeur à cinq.
   *
   * Les classes `piste`, `case-j` et `graduations` viennent du design system
   * (`base.css`) : c'est son composant, pas un second dessin de la même chose.
   */
  let {
    valeur,
    max,
    jusqua = 0,
    graduations = false,
    libelle,
    famille = 'chance',
    onchange,
  }: {
    valeur: number
    max: number
    /**
     * Nombre total de cases à dessiner, plafond compris.
     *
     * Celles qui dépassent le plafond du personnage sont tracées en pointillés
     * et ne se cliquent pas : elles disent ce que la Jauge deviendra quand la
     * Compétence qui la porte aura grandi.
     */
    jusqua?: number
    /** Une échelle sous la piste, tous les cinq emplacements. */
    graduations?: boolean
    libelle: string
    /** Décide de la couleur d'une case pleine. Chaque jauge a la sienne. */
    famille?: 'chance' | 'stress' | 'contam'
    onchange: (v: number) => void
  } = $props()

  const cases = $derived(Math.max(max, jusqua))
  const paliers = $derived(
    Array.from({ length: Math.floor(cases / 5) }, (_, i) => (i + 1) * 5),
  )

  /** Cliquer la dernière case pleine la vide : sans quoi on ne redescend jamais. */
  const viser = (rang: number) => onchange(rang === valeur ? rang - 1 : rang)
</script>

{#if cases === 0}
  <p class="aucun">Aucun emplacement.</p>
{:else}
  <div class="voie" style:--cases={cases}>
    <div class="piste jauge-{famille}" role="group" aria-label={libelle}>
      {#each Array.from({ length: cases }, (_, i) => i + 1) as rang (rang)}
        {#if rang <= max}
          <button
            type="button"
            class="case-j"
            aria-label="Mettre {libelle} à {rang}"
            aria-pressed={rang <= valeur}
            onclick={() => viser(rang)}
          ></button>
        {:else}
          <span class="case-j" data-hors-plafond="1" title="Au-delà de la Jauge actuelle"></span>
        {/if}
      {/each}
    </div>
    {#if graduations && paliers.length > 1}
      <div class="graduations" aria-hidden="true">
        {#each paliers as palier (palier)}
          <span>{palier}</span>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style>
  /* Le design system dessine des cases élastiques, pensées pour une Jauge de
     Stress à onze emplacements. À deux, elles deviendraient deux bannières :
     on borne donc la piste entière plutôt que chaque case — sans quoi les
     graduations, elles, continueraient jusqu'au bout du cartouche et
     désigneraient le vide. Les deux partagent maintenant la même longueur. */
  .voie {
    display: grid;
    gap: 6px;
    max-width: calc(var(--cases) * 2.4rem + (var(--cases) - 1) * 3px);
  }
  .aucun {
    margin: 0;
    font-size: 0.8rem;
    color: var(--ink-soft);
  }
</style>
